// docs/34 SX10 — the interface's sounds, by delegation: one set of document listeners hears every
// button, tab, menu row, toggle, slider and card in every screen, so a new screen built from the kit
// (docs/16) sounds right without a line of audio code. What an element sounds like:
//
//   data-sfx="none"            silent (it plays something richer itself, or nothing should sound)
//   data-sfx="ui.coins"        that bank (any SfxKey; a literal here also ships the bank)
//   role=tab                   ui.tab          role=menuitem / interactive card   ui.click
//   checkbox, radio, toggle,
//   segmented option, select   ui.toggle       range slider                       ui.toggle, pitched by value
//   .fk-btn--primary/--danger  ui.confirm      .fk-btn--ghost                     ui.back
//   any other button           ui.click
//   wheel over [data-sfx-scroll]  ui.scroll, a soft loop while the list moves (the Saga, the Chronicle)
//
// Hovering a live button, card, tab or menu row ticks (`ui.hover`, mouse only). Toasts, modals and
// screen changes sound from their stores (a toast stays quiet when its moment already sounded). Everything here is local: only this player hears it.
import { audio, playSfx, startLoop, stopLoop, type SfxKey } from '../core/audio';
import { deviceNow } from '../core/time';
import { useToasts } from '../core/net/reducers';
import { useRoute, type Screen } from '../core/stores/route';
import { SFX_KEYS } from '../core/audio/keys.gen';
import { useSettings } from '../core/stores/settings';

/** Reward moments (a level-up, an item, the loot) in the player's chosen voice: 8-bit under Settings › Retro chimes. */
const RETRO: ReadonlySet<SfxKey> = new Set<SfxKey>(['ui.levelup', 'ui.item', 'ui.loot', 'ui.loot_rare', 'ui.gems', 'ui.potion']);
export function rewardSfx(key: SfxKey): SfxKey {
  return RETRO.has(key) && useSettings.getState().audio.retro ? 'ui.retro' : key;
}

const KEYS: ReadonlySet<string> = new Set(SFX_KEYS);

const CLICKABLE = 'button, [role="menuitem"], [role="tab"], a[href], summary, input[type="checkbox"], input[type="radio"], [data-sfx]';
const HOVERABLE = '.fk-btn, [role="menuitem"], [role="tab"], .fk-card--interactive, .fk-segmented__opt';

/** What clicking `el` sounds like, or undefined for silence. */
export function clickSound(el: Element): SfxKey | undefined {
  const own = el.closest('[data-sfx]')?.getAttribute('data-sfx');
  if (own === 'none') return undefined;
  if (own && KEYS.has(own)) return rewardSfx(own as SfxKey);
  if ((el as HTMLButtonElement).disabled || el.getAttribute('aria-disabled') === 'true') return undefined;
  const role = el.getAttribute('role');
  if (role === 'tab') return 'ui.tab';
  if (el instanceof HTMLInputElement && (el.type === 'checkbox' || el.type === 'radio')) return 'ui.toggle';
  if (el.classList.contains('fk-segmented__opt')) return 'ui.toggle';
  if (el.classList.contains('fk-btn--primary') || el.classList.contains('fk-btn--danger')) return 'ui.confirm';
  if (el.classList.contains('fk-btn--ghost')) return 'ui.back';
  return 'ui.click';
}

/** Screens that open with their own sound (the fight's foe, the tale's stinger) skip the swish. */
const OWN_ENTRANCE: ReadonlySet<Screen> = new Set(['encounter', 'intro', 'writing', 'epilogue', 'chapter', 'title']);

/** The interface's own clicks and ticks: they never stand in for a moment's sound. */
const CHROME: ReadonlySet<string> = new Set(['ui.click', 'ui.hover', 'ui.confirm', 'ui.back', 'ui.toggle', 'ui.tab', 'ui.type', 'ui.scroll', 'ui.open', 'ui.close', 'ui.transition']);
const MOMENT_MS = 350;
/**
 * Did a moment sound locally from `MOMENT_MS` before `at` until now: an interface-bus sound that is not
 * the chrome (coins, a sale, an item's own sound, an error)? Footsteps, the inn's life and the beds
 * around it are other buses and never silence a toast.
 */
function momentSounded(at: number): boolean {
  const h = audio.history;
  for (let i = h.length - 1; i >= 0; i--) {
    const r = h[i]!;
    if (r.at < at - MOMENT_MS) break;
    if (r.origin === 'local' && !r.dropped && !CHROME.has(r.key) && audio.bank(r.key)?.bus === 'ui') return true;
  }
  return false;
}

let installed: (() => void) | undefined;

export function installUiSounds(): () => void {
  if (installed) return installed;
  let hovered: Element | null = null;
  const onClick = (e: MouseEvent) => {
    const el = (e.target as Element | null)?.closest?.(CLICKABLE);
    if (!el) return;
    const key = clickSound(el);
    if (key) playSfx(key);
  };
  const onOver = (e: PointerEvent) => {
    if (e.pointerType !== 'mouse') return;
    const el = (e.target as Element | null)?.closest?.(HOVERABLE) ?? null;
    if (el === hovered) return;
    hovered = el;
    if (!el || (el as HTMLButtonElement).disabled || el.closest('[data-sfx="none"]')) return;
    playSfx('ui.hover');
  };
  const onInput = (e: Event) => {
    const el = e.target;
    if (el instanceof HTMLInputElement && el.type === 'range' && !el.closest('[data-sfx="none"]')) {
      const span = Number(el.max || 100) - Number(el.min || 0) || 1;
      playSfx('ui.toggle', { rate: 0.9 + (0.25 * (Number(el.value) - Number(el.min || 0))) / span, gain: 0.7 });
    }
  };
  const onChange = (e: Event) => { if (e.target instanceof HTMLSelectElement && !e.target.closest('[data-sfx="none"]')) playSfx('ui.toggle'); };
  let scrolling: ReturnType<typeof setTimeout> | undefined;
  const onWheel = (e: WheelEvent) => {
    if (!(e.target as Element | null)?.closest?.('[data-sfx-scroll]')) return;
    if (!scrolling) startLoop('ui-scroll', 'ui.scroll', { gain: 0.6, fadeMs: 120 });
    clearTimeout(scrolling);
    scrolling = setTimeout(() => { stopLoop('ui-scroll', 260); scrolling = undefined; }, 240);
  };
  document.addEventListener('click', onClick, true);
  document.addEventListener('pointerover', onOver, true);
  document.addEventListener('input', onInput, true);
  document.addEventListener('change', onChange, true);
  document.addEventListener('wheel', onWheel, { capture: true, passive: true });
  // a toast's tone decides its sound, unless the moment it reports has just sounded itself (a sale's
  // coins, a craft's hammer, an error already buzzed): then the toast stays quiet rather than stacking
  // a second chime on the first. Keyed by the newest toast's id, so a full stack (five shown) still sounds.
  let lastToast = useToasts.getState().toasts.at(-1)?.id ?? 0;
  const unToast = useToasts.subscribe((s) => {
    const t = s.toasts.at(-1);
    if (!t || t.id <= lastToast) return;
    lastToast = t.id;
    const key: SfxKey = t.tone === 'error' ? 'ui.error' : t.tone === 'success' ? 'ui.confirm' : 'ui.toast';
    const pushedAt = deviceNow();
    // a tick later, so a sound played just after the push (in the same handler) also counts
    setTimeout(() => { if (!momentSounded(pushedAt)) playSfx(key, { gain: t.tone === 'success' ? 0.6 : 1 }); }, 0);
  });
  const unRoute = useRoute.subscribe((s, p) => { if (s.screen !== p.screen && !OWN_ENTRANCE.has(s.screen)) playSfx('ui.transition'); });
  installed = () => {
    document.removeEventListener('click', onClick, true);
    document.removeEventListener('pointerover', onOver, true);
    document.removeEventListener('input', onInput, true);
    document.removeEventListener('change', onChange, true);
    document.removeEventListener('wheel', onWheel, true);
    unToast(); unRoute();
    installed = undefined;
  };
  return installed;
}
